import { AxiosError, isAxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectDeviceToken, setShowNavigator } from '../../appSlice';
import { ReactComponent as ErrorIcon } from '../../assets/icons/RedError.svg';
import { generateHeaders } from '../../utils/api';
import { axios } from '../../utils/axios';
import { SecureStorageKeys } from '../../utils/enums';
import { saveData, setStatusBarLight } from '../../utils/webview/messages';
import {
    selectLoginError,
    selectLoginPhone,
    selectLoginToken,
    setLoginError,
    setLoginToken,
} from './slice';

interface MfaFormValues {
    code: string[];
}

const MfaLogin = () => {
    const dispatch = useAppDispatch();
    const token = useAppSelector(selectLoginToken);
    const phone = useAppSelector(selectLoginPhone);
    const deviceToken = useAppSelector(selectDeviceToken);
    // Use redux to capture SSO errors too
    const loginError = useAppSelector(selectLoginError);
    const [searchParams] = useSearchParams();
    const realm =
        searchParams.get('realm') && searchParams.get('realm') !== 'undefined'
            ? searchParams.get('realm')
            : undefined;
    const [loading, setLoading] = useState(false);
    const [codeExpirationSeconds, setCodeExpirationSeconds] = useState(10 * 60); // Twilio 10 minutes
    const inputRefs = [
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
    ];
    const formRef = useRef<HTMLFormElement>(null);

    useEffect(() => {
        dispatch(setShowNavigator(false));
        setStatusBarLight(true);
    }, [dispatch]);

    useEffect(() => {
        const interval = setInterval(
            () => setCodeExpirationSeconds(codeExpirationSeconds - 1),
            1000
        );

        return () => clearInterval(interval);
    });

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        formState: { errors },
    } = useForm<MfaFormValues>({
        defaultValues: {
            code: ['', '', '', '', '', ''],
        },
    });

    const onSubmit = async (data: MfaFormValues) => {
        try {
            dispatch(setLoginError(null));
            setLoading(true);
            const result = await axios.post(
                `${process.env.REACT_APP_AUTH_ENDPOINT}/mfa/verify`,
                {
                    // Or undefined for simulators, otherwise null is passed and errors
                    deviceId: deviceToken || undefined,
                    token,
                    code: data.code.join(''),
                    ...(realm ? { realm } : {}),
                },
                { headers: await generateHeaders() }
            );
            if (isAxiosError(result.data?.axiosError))
                throw result.data.axiosError;
            saveData(SecureStorageKeys.ACCESS_TOKEN, result.data.access_token);
            saveData(
                SecureStorageKeys.REFRESH_TOKEN,
                result.data.refresh_token
            );
        } catch (e) {
            let message = 'Something went wrong. Please try again.';
            if (isAxiosError(e)) {
                const axiosError = e as AxiosError<any>;
                if (axiosError.response?.status === 401) {
                    message = 'Invalid credentials. Please try again.';
                }
            }
            dispatch(setLoginError(message));
        } finally {
            setLoading(false);
        }
    };

    const onResend = async () => {
        try {
            dispatch(setLoginError(null));
            setLoading(true);
            const result = await axios.post(
                `${process.env.REACT_APP_AUTH_ENDPOINT}/mfa/resend`,
                { token },
                { headers: await generateHeaders() }
            );
            if (isAxiosError(result.data?.axiosError))
                throw result.data.axiosError;
            if (result.data.token) {
                dispatch(setLoginToken(result.data.token));
            }
            setCodeExpirationSeconds(10 * 60);
        } catch (e) {
            let message = 'Something went wrong. Please try again.';
            if (isAxiosError(e)) {
                const axiosError = e as AxiosError<any>;
                if (axiosError.response?.status === 401) {
                    message = 'Invalid credentials. Please try again.';
                }
            }
            dispatch(setLoginError(message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <h1
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'end',
                    paddingBottom: 50,
                }}
                id="login_page_header"
            >
                Code verification
            </h1>
            <form
                className="form"
                onSubmit={handleSubmit(onSubmit)}
                ref={formRef}
            >
                <p>A 6-digit code has been sent to +1 (XXX)-XXX-{phone}.</p>
                <div
                    style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        justifyContent: 'space-around',
                    }}
                >
                    {[...Array(6)].map((_, i) => (
                        <input
                            key={`mfa-code-${i}`}
                            type="number"
                            className="input input--mfa_code"
                            {...register(`code.${i}`, {
                                required:
                                    'The code is invalid. Please try again',
                                onChange: (e) => {
                                    setValue(
                                        `code.${i}`,
                                        e.target.value.split().pop()
                                    );
                                    if (e.target.value.length) {
                                        if (i < 5) {
                                            inputRefs[i + 1].current?.focus();
                                            inputRefs[i + 1].current?.select();
                                        } else onSubmit(getValues());
                                    }
                                },
                            })}
                            ref={inputRefs[i]}
                            maxLength={1}
                            onKeyDown={(
                                event: React.KeyboardEvent<HTMLInputElement>
                            ) => {
                                if (event.key === 'Backspace') {
                                    if (i > 0) {
                                        inputRefs[i - 1].current?.focus();
                                        inputRefs[i - 1].current?.select();
                                    }
                                }
                            }}
                        />
                    ))}
                </div>
                {errors.code?.length && (
                    <span className="form__error">{errors.code.message}</span>
                )}
                {loginError && (
                    <div className="radio-error" style={{ marginTop: 5 }}>
                        <ErrorIcon
                            className="radio-error-icon"
                            id="login_page_error_icon"
                        />
                        {loginError}
                    </div>
                )}
                <button
                    type="submit"
                    className="button"
                    disabled={loading}
                    id="login_page_button"
                >
                    Log in
                </button>
            </form>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 2,
                    justifyContent: 'space-evenly',
                }}
            >
                <p style={{ textAlign: 'center', fontSize: 16 }}>
                    The code will expire in{' '}
                    <strong>
                        {Math.floor(Math.max(codeExpirationSeconds, 0) / 60)
                            .toString()
                            .padStart(2, '0')}
                        :
                        {(Math.max(codeExpirationSeconds, 0) % 60)
                            .toString()
                            .padStart(2, '0')}
                    </strong>
                    .
                    <br />
                    Didn't receive the code?{' '}
                    <u
                        style={{ cursor: loading ? 'default' : 'pointer' }}
                        onClick={loading ? undefined : onResend}
                        id="mfa_resend"
                    >
                        Resend
                    </u>
                    .
                </p>
            </div>
        </>
    );
};

export default MfaLogin;
